import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { compareByEffectiveIbanStatus, getCleanBankAccountNumber, isBankAccountIbanSafe } from './accounting/bankutils';
import {
  addCalendarDate,
  CalendarDate,
  calendarDateFromTimestamp,
  createCalendarDate,
  isAfter,
  isAfterOrEqual,
  isBefore,
  subtractCalendarDate,
  toCalendarDate,
} from './dateutils';
import { roundAtSecondDecimal } from './numbers';
import { isNilOrEmpty } from './objectUtils';
import {
  BankAccount,
  BankAccountType,
  OpenInvoiceIbanState,
  PaymentInitiationChannel,
  PaymentOrder,
  PaymentOrderAnomaly,
  PaymentOrderStatus,
  PaymentOrderType,
} from './types/model-app';

export const CONFIRMATION_DEADLINE_DEFAULT_BUSINESS_DAYS = 2;

export const CANCEL_REASON_CODE_MAX_LENGTH = 35;

const LAST_BUSINESS_DAY_OF_WEEK = 5;
const GOOD_FRIDAY_OFFSET_DAYS = -2;
const EASTER_MONDAY_OFFSET_DAYS = 1;
const TARGET2_FIXED_CLOSING_DAYS: Array<{ month: number; day: number }> = [
  { month: 1, day: 1 },
  { month: 5, day: 1 },
  { month: 12, day: 25 },
  { month: 12, day: 26 },
];

/**
 * Easter Sunday of a Gregorian year, with the anonymous Gregorian algorithm (Meeus/Jones/Butcher):
 * https://en.wikipedia.org/wiki/Date_of_Easter#Anonymous_Gregorian_algorithm
 */
export const easterSundayOf = (year: number): CalendarDate => {
  const yearInMetonicCycle = year % 19;
  const century = Math.floor(year / 100);
  const yearInCentury = year % 100;
  const leapCenturies = Math.floor(century / 4);
  const centuryInLeapCycle = century % 4;
  const moonOrbitCorrection = Math.floor((century + 8) / 25);
  const lunarCorrection = Math.floor((century - moonOrbitCorrection + 1) / 3);
  const daysFromMarch21ToFullMoon = (19 * yearInMetonicCycle + century - leapCenturies - lunarCorrection + 15) % 30;
  const leapYearsInCentury = Math.floor(yearInCentury / 4);
  const yearInLeapCycle = yearInCentury % 4;
  const daysFromFullMoonToSunday =
    (32 + 2 * centuryInLeapCycle + 2 * leapYearsInCentury - daysFromMarch21ToFullMoon - yearInLeapCycle) % 7;
  const lateFullMoonShift = Math.floor(
    (yearInMetonicCycle + 11 * daysFromMarch21ToFullMoon + 22 * daysFromFullMoonToSunday) / 451
  );
  const encodedMonthAndDay = daysFromMarch21ToFullMoon + daysFromFullMoonToSunday - 7 * lateFullMoonShift + 114;
  return createCalendarDate(year, Math.floor(encodedMonthAndDay / 31), (encodedMonthAndDay % 31) + 1);
};

/**
 * TARGET2 closing days, the days SEPA credit transfers do not settle: 1 January, Good Friday, Easter Monday,
 * 1 May, 25 and 26 December.
 */
export const isTarget2ClosingDay = (date: CalendarDate): boolean => {
  if (TARGET2_FIXED_CLOSING_DAYS.some((closing) => closing.month === date.month && closing.day === date.day)) {
    return true;
  }
  const easterSunday = easterSundayOf(date.year);
  const goodFriday = addCalendarDate(easterSunday, GOOD_FRIDAY_OFFSET_DAYS, 'days');
  const easterMonday = addCalendarDate(easterSunday, EASTER_MONDAY_OFFSET_DAYS, 'days');
  return date.equals(goodFriday) || date.equals(easterMonday);
};

export const isPaymentBusinessDay = (date: CalendarDate): boolean =>
  date.dayOfWeek <= LAST_BUSINESS_DAY_OF_WEEK && !isTarget2ClosingDay(date);

/** A transfer never executes before the day it is handled: the later of the due date and the reference day. */
export const effectiveExecutionDate = (dueDate: CalendarDate, referenceDate: CalendarDate): CalendarDate =>
  isAfter(referenceDate, dueDate) ? referenceDate : dueDate;

export const addPaymentBusinessDays = (date: CalendarDate, businessDays: number): CalendarDate => {
  let remaining = businessDays;
  let current = date;
  while (remaining > 0) {
    current = addCalendarDate(current, 1, 'days');
    if (isPaymentBusinessDay(current)) {
      remaining -= 1;
    }
  }
  return current;
};

export type PaymentOrderTransitionActor = 'human' | 'system';

export interface PaymentOrderTransitionContext {
  actor: PaymentOrderTransitionActor;
  channel?: PaymentInitiationChannel;
  rejectedReason?: string;
}

export const PAYMENT_ORDER_TRANSITIONS: Record<PaymentOrderStatus, PaymentOrderStatus[]> = {
  [PaymentOrderStatus.SUGGESTED]: [
    PaymentOrderStatus.TO_VALIDATE,
    PaymentOrderStatus.APPROVED,
    PaymentOrderStatus.BLOCKED,
  ],
  [PaymentOrderStatus.TO_VALIDATE]: [
    PaymentOrderStatus.APPROVED,
    PaymentOrderStatus.REJECTED,
    PaymentOrderStatus.BLOCKED,
  ],
  [PaymentOrderStatus.APPROVED]: [PaymentOrderStatus.ISSUED, PaymentOrderStatus.EXECUTED, PaymentOrderStatus.BLOCKED],
  [PaymentOrderStatus.ISSUED]: [PaymentOrderStatus.EXECUTED, PaymentOrderStatus.CANCELLED],
  [PaymentOrderStatus.EXECUTED]: [
    PaymentOrderStatus.RECONCILED,
    PaymentOrderStatus.APPROVED,
    PaymentOrderStatus.ISSUED,
  ],
  [PaymentOrderStatus.RECONCILED]: [PaymentOrderStatus.EXECUTED],
  [PaymentOrderStatus.REJECTED]: [PaymentOrderStatus.TO_VALIDATE, PaymentOrderStatus.CANCELLED],
  [PaymentOrderStatus.BLOCKED]: [
    PaymentOrderStatus.SUGGESTED,
    PaymentOrderStatus.TO_VALIDATE,
    PaymentOrderStatus.APPROVED,
  ],
  [PaymentOrderStatus.CANCELLED]: [],
};

const SYSTEM_ONLY_TARGET_STATUSES = [PaymentOrderStatus.BLOCKED, PaymentOrderStatus.EXECUTED];

const SYSTEM_ONLY_SOURCE_STATUSES = [PaymentOrderStatus.EXECUTED, PaymentOrderStatus.RECONCILED];

const isManualExecutionConfirmation = (fromStatus: PaymentOrderStatus, toStatus: PaymentOrderStatus): boolean =>
  fromStatus === PaymentOrderStatus.ISSUED && toStatus === PaymentOrderStatus.EXECUTED;

export enum PaymentOrderTransitionDeniedCode {
  TRANSITION_NOT_IN_LIFECYCLE = 'TRANSITION_NOT_IN_LIFECYCLE',
  SYSTEM_ONLY_TRANSITION = 'SYSTEM_ONLY_TRANSITION',
  UNBLOCK_IS_SYSTEM_ONLY = 'UNBLOCK_IS_SYSTEM_ONLY',
  REJECTION_COMMENT_REQUIRED = 'REJECTION_COMMENT_REQUIRED',
  REJECTION_IS_HUMAN_ONLY = 'REJECTION_IS_HUMAN_ONLY',
  CHANNEL_REQUIRED_TO_ISSUE = 'CHANNEL_REQUIRED_TO_ISSUE',
}

export interface PaymentOrderTransitionCheck {
  allowed: boolean;
  deniedCode?: PaymentOrderTransitionDeniedCode;
}

const getPaymentOrderTransitionDeniedCode = (
  fromStatus: PaymentOrderStatus,
  toStatus: PaymentOrderStatus,
  context: PaymentOrderTransitionContext
): PaymentOrderTransitionDeniedCode | undefined => {
  if (!PAYMENT_ORDER_TRANSITIONS[fromStatus].includes(toStatus)) {
    return PaymentOrderTransitionDeniedCode.TRANSITION_NOT_IN_LIFECYCLE;
  }
  if (
    (SYSTEM_ONLY_TARGET_STATUSES.includes(toStatus) || SYSTEM_ONLY_SOURCE_STATUSES.includes(fromStatus)) &&
    context.actor !== 'system' &&
    !isManualExecutionConfirmation(fromStatus, toStatus)
  ) {
    return PaymentOrderTransitionDeniedCode.SYSTEM_ONLY_TRANSITION;
  }
  if (fromStatus === PaymentOrderStatus.BLOCKED && context.actor !== 'system') {
    return PaymentOrderTransitionDeniedCode.UNBLOCK_IS_SYSTEM_ONLY;
  }
  if (toStatus === PaymentOrderStatus.REJECTED && isNilOrEmpty(context.rejectedReason?.trim())) {
    return PaymentOrderTransitionDeniedCode.REJECTION_COMMENT_REQUIRED;
  }
  if (toStatus === PaymentOrderStatus.REJECTED && context.actor !== 'human') {
    return PaymentOrderTransitionDeniedCode.REJECTION_IS_HUMAN_ONLY;
  }
  if (toStatus === PaymentOrderStatus.ISSUED && isNil(context.channel)) {
    return PaymentOrderTransitionDeniedCode.CHANNEL_REQUIRED_TO_ISSUE;
  }
  return undefined;
};

export const checkPaymentOrderTransition = (
  fromStatus: PaymentOrderStatus,
  toStatus: PaymentOrderStatus,
  context: PaymentOrderTransitionContext
): PaymentOrderTransitionCheck => {
  const deniedCode = getPaymentOrderTransitionDeniedCode(fromStatus, toStatus, context);
  if (isNil(deniedCode)) {
    return { allowed: true };
  }
  return { allowed: false, deniedCode };
};

export const canTransitionPaymentOrder = (
  fromStatus: PaymentOrderStatus,
  toStatus: PaymentOrderStatus,
  context: PaymentOrderTransitionContext
): boolean => checkPaymentOrderTransition(fromStatus, toStatus, context).allowed;

export const assertPaymentOrderTransition = (
  fromStatus: PaymentOrderStatus,
  toStatus: PaymentOrderStatus,
  context: PaymentOrderTransitionContext
): void => {
  const check = checkPaymentOrderTransition(fromStatus, toStatus, context);
  if (!check.allowed) {
    throw new Error(check.deniedCode);
  }
};

export const PAYMENT_TIME_ZONE = 'Europe/Brussels';

/** The day an issued order leaves its account: its due date, or the day it was issued when issued after it. */
export const getIssuedPaymentOrderExecutionDate = (
  order: Pick<PaymentOrder, 'dueDate' | 'issuedAt' | 'updatedAt'>
): CalendarDate => {
  const dueDate = toCalendarDate(order.dueDate);
  const issuedAt = order.issuedAt ?? order.updatedAt;
  if (isNil(issuedAt)) {
    return dueDate;
  }
  return effectiveExecutionDate(dueDate, calendarDateFromTimestamp(issuedAt, PAYMENT_TIME_ZONE));
};

export const computeConfirmationDeadline = (
  channel: PaymentInitiationChannel,
  issuedOn: CalendarDate,
  chosenDate?: CalendarDate
): CalendarDate => {
  switch (channel) {
    case PaymentInitiationChannel.API_PUSH:
      return issuedOn;
    case PaymentInitiationChannel.SEPA_FILE:
      return addPaymentBusinessDays(issuedOn, CONFIRMATION_DEADLINE_DEFAULT_BUSINESS_DAYS);
    case PaymentInitiationChannel.MANUAL:
      return chosenDate ?? addPaymentBusinessDays(issuedOn, CONFIRMATION_DEADLINE_DEFAULT_BUSINESS_DAYS);
    default:
      return addPaymentBusinessDays(issuedOn, CONFIRMATION_DEADLINE_DEFAULT_BUSINESS_DAYS);
  }
};

export const isValidManualConfirmationDeadline = (deadline: CalendarDate, issuedOn: CalendarDate): boolean =>
  !isBefore(deadline, issuedOn);

export type ConfirmationOverdueCheckFields = Pick<PaymentOrder, 'status' | 'confirmationDeadlineDate'>;

export const isConfirmationOverdue = (order: ConfirmationOverdueCheckFields, referenceDate: CalendarDate): boolean => {
  if (order.status !== PaymentOrderStatus.ISSUED || isNil(order.confirmationDeadlineDate)) {
    return false;
  }
  return isAfter(referenceDate, toCalendarDate(order.confirmationDeadlineDate));
};

export const PLANNED_PAYMENT_ORDER_STATUSES: PaymentOrderStatus[] = [
  PaymentOrderStatus.SUGGESTED,
  PaymentOrderStatus.TO_VALIDATE,
  PaymentOrderStatus.APPROVED,
  PaymentOrderStatus.ISSUED,
];

export const TERMINAL_PAYMENT_ORDER_STATUSES: PaymentOrderStatus[] = [
  PaymentOrderStatus.RECONCILED,
  PaymentOrderStatus.CANCELLED,
];

export const NON_TERMINAL_PAYMENT_ORDER_STATUSES: PaymentOrderStatus[] = Object.values(PaymentOrderStatus).filter(
  (status) => !TERMINAL_PAYMENT_ORDER_STATUSES.includes(status)
);

export const RECENTLY_RECONCILED_DAYS = 30;

export const getRecentlyReconciledSince = (referenceDate: CalendarDate): CalendarDate =>
  subtractCalendarDate(referenceDate, RECENTLY_RECONCILED_DAYS, 'days');

export const isReconciledSince = (order: Pick<PaymentOrder, 'reconciledAt' | 'status'>, since: CalendarDate): boolean =>
  order.status === PaymentOrderStatus.RECONCILED &&
  !isNil(order.reconciledAt) &&
  isAfterOrEqual(calendarDateFromTimestamp(order.reconciledAt, PAYMENT_TIME_ZONE), since);

export const AWAITING_DEBIT_PAYMENT_ORDER_STATUSES: PaymentOrderStatus[] = [
  PaymentOrderStatus.APPROVED,
  PaymentOrderStatus.ISSUED,
];

export type ManualConfirmationOrderFields = Pick<PaymentOrder, 'executedBy' | 'executedTransactionId' | 'status'>;

export const isConfirmedManuallyAwaitingDebit = (order: ManualConfirmationOrderFields): boolean =>
  order.status === PaymentOrderStatus.EXECUTED && !isNil(order.executedBy) && isNil(order.executedTransactionId);

export const isAwaitingDebit = (order: ManualConfirmationOrderFields): boolean =>
  AWAITING_DEBIT_PAYMENT_ORDER_STATUSES.includes(order.status) || isConfirmedManuallyAwaitingDebit(order);

export const isCommittedOutflow = (order: ManualConfirmationOrderFields): boolean =>
  PLANNED_PAYMENT_ORDER_STATUSES.includes(order.status) || isConfirmedManuallyAwaitingDebit(order);

export type CollectedFeeOrderFields = Pick<PaymentOrder, 'feeArrearsId' | 'requestedAmount'>;

export const isCollectedFee = (order: CollectedFeeOrderFields): boolean =>
  !isNil(order.requestedAmount) || !isNil(order.feeArrearsId);

export type CancelledAfterIssuanceOrderFields = Pick<PaymentOrder, 'channel' | 'issuedAt' | 'status'>;

export const isCancelledAfterIssuance = (order: CancelledAfterIssuanceOrderFields): boolean =>
  order.status === PaymentOrderStatus.CANCELLED && !isNil(order.issuedAt) && !isNil(order.channel);

export type ReissuableOrderFields = CancelledAfterIssuanceOrderFields &
  CollectedFeeOrderFields &
  Pick<PaymentOrder, 'reissuedOrderId'>;

export const isReissuableOrder = (order: ReissuableOrderFields): boolean =>
  isCancelledAfterIssuance(order) && !isCollectedFee(order) && isNil(order.reissuedOrderId);

export type ProjectedBalanceOrderFields = Pick<
  PaymentOrder,
  'amount' | 'executedBy' | 'executedTransactionId' | 'status'
>;

export type ProjectedBalanceAccountOrderFields = Pick<
  PaymentOrder,
  'amount' | 'bankAccountId' | 'executedBy' | 'executedTransactionId' | 'status'
>;

// Funding-check invariant (00-architecture §7): committed outflows only, never expected revenue.
export const computeProjectedBalance = (
  balance: number | null | undefined,
  plannedOrders: ProjectedBalanceOrderFields[]
): number | null => {
  if (isNil(balance)) {
    return null;
  }
  const committedOutflows = plannedOrders.reduce(
    (acc, order) => (isCommittedOutflow(order) ? acc + order.amount : acc),
    0
  );
  return roundAtSecondDecimal(balance - committedOutflows);
};

export type ProjectedBalanceAccountFields = Pick<BankAccount, 'id' | 'balance'>;

/** ONE projected-balance implementation, shared by the open-invoices funds check and the treasury widget. */
export const computeProjectedBalances = (
  bankAccounts: ProjectedBalanceAccountFields[],
  orders: ProjectedBalanceAccountOrderFields[]
): Map<string, number | null> => {
  const projectedByAccountId = new Map<string, number | null>();
  bankAccounts.forEach((bankAccount) => {
    const accountOrders = orders.filter((order) => order.bankAccountId === bankAccount.id);
    projectedByAccountId.set(bankAccount.id, computeProjectedBalance(bankAccount.balance, accountOrders));
  });
  return projectedByAccountId;
};

export const SETTLED_PAYMENT_ORDER_STATUSES: PaymentOrderStatus[] = [
  PaymentOrderStatus.EXECUTED,
  PaymentOrderStatus.RECONCILED,
];

export type PaymentOrderHistoryPoint = Pick<
  PaymentOrder,
  'amount' | 'counterpartAccountNumber' | 'dueDate' | 'id' | 'issuedAt' | 'status'
>;

export interface PaymentOrderAnomalyDetectionOptions {
  windowSize: number;
  minSamples: number;
  outlierFactor: number;
}

export const PAYMENT_ORDER_ANOMALY_DETECTION_DEFAULTS: PaymentOrderAnomalyDetectionOptions = {
  windowSize: 12,
  minSamples: 3,
  outlierFactor: 3,
};

export const PAYMENT_ORDER_ANOMALY_HISTORY_READ_LIMIT = PAYMENT_ORDER_ANOMALY_DETECTION_DEFAULTS.windowSize * 2;

export interface PaymentOrderAnomalyDetection {
  anomalies: PaymentOrderAnomaly[];
  previousIban?: string;
  referenceAmount?: number;
}

export const computeMedian = (values: number[]): number | undefined => {
  if (isEmpty(values)) {
    return undefined;
  }
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) {
    return sorted[middle];
  }
  return roundAtSecondDecimal((sorted[middle - 1] + sorted[middle]) / 2);
};

const settlementKey = (point: PaymentOrderHistoryPoint): string => point.issuedAt ?? point.dueDate;

const compareHistoryNewestFirst = (left: PaymentOrderHistoryPoint, right: PaymentOrderHistoryPoint): number => {
  const leftKey = settlementKey(left);
  const rightKey = settlementKey(right);
  if (leftKey !== rightKey) {
    return leftKey < rightKey ? 1 : -1;
  }
  if (left.id === right.id) {
    return 0;
  }
  return left.id < right.id ? 1 : -1;
};

export type AnomalyDetectionOrderFields = Pick<PaymentOrder, 'amount' | 'counterpartAccountNumber' | 'id'>;

export const detectPaymentOrderAnomalies = (
  order: AnomalyDetectionOrderFields,
  history: PaymentOrderHistoryPoint[],
  options: PaymentOrderAnomalyDetectionOptions = PAYMENT_ORDER_ANOMALY_DETECTION_DEFAULTS
): PaymentOrderAnomalyDetection => {
  const settled = history
    .filter((point) => point.id !== order.id && SETTLED_PAYMENT_ORDER_STATUSES.includes(point.status))
    .sort(compareHistoryNewestFirst);
  if (isEmpty(settled)) {
    return { anomalies: [] };
  }
  const detection: PaymentOrderAnomalyDetection = { anomalies: [] };
  const lastPayment = settled[0];
  if (
    getCleanBankAccountNumber(lastPayment.counterpartAccountNumber) !==
    getCleanBankAccountNumber(order.counterpartAccountNumber)
  ) {
    detection.anomalies.push(PaymentOrderAnomaly.IBAN_CHANGED);
    detection.previousIban = lastPayment.counterpartAccountNumber;
  }
  const samples = settled.slice(0, options.windowSize).map((point) => point.amount);
  const median = samples.length >= options.minSamples ? computeMedian(samples) : undefined;
  if (!isNil(median) && median > 0 && order.amount > options.outlierFactor * median) {
    detection.anomalies.push(PaymentOrderAnomaly.AMOUNT_OUTLIER);
    detection.referenceAmount = median;
  }
  return detection;
};

export type BeneficiaryIbanAccountFields = Pick<
  BankAccount,
  'contactId' | 'disabled' | 'ibanStatus' | 'ibanVerifiedNumber' | 'id' | 'number' | 'type'
>;

export type BeneficiaryIbanOrderFields = Pick<
  PaymentOrder,
  'counterpartAccountNumber' | 'counterpartContactId' | 'type'
>;

const BENEFICIARY_ACCOUNT_TYPES: Partial<Record<PaymentOrderType, BankAccountType[]>> = {
  [PaymentOrderType.SUPPLIER]: [BankAccountType.SUPPLIER_ACCOUNT],
};

const isBeneficiaryAccountOfType = (account: BeneficiaryIbanAccountFields, type: PaymentOrderType): boolean => {
  const allowedTypes = BENEFICIARY_ACCOUNT_TYPES[type];
  return isNil(allowedTypes) || allowedTypes.includes(account.type);
};

export interface BeneficiaryIbanResolution {
  bankAccountId?: string;
  replaced: boolean;
  state: OpenInvoiceIbanState;
}

export const resolveBeneficiaryIbanState = (
  order: BeneficiaryIbanOrderFields,
  accounts: BeneficiaryIbanAccountFields[]
): BeneficiaryIbanResolution => {
  const cleanIban = getCleanBankAccountNumber(order.counterpartAccountNumber);
  const candidates = accounts.filter(
    (account) => !account.disabled && getCleanBankAccountNumber(account.number) === cleanIban
  );
  const account = isNil(order.counterpartContactId)
    ? candidates[0]
    : candidates.find(
        (candidate) =>
          candidate.contactId === order.counterpartContactId && isBeneficiaryAccountOfType(candidate, order.type)
      );
  if (isNil(account)) {
    const beneficiaryHasAnotherAccount =
      !isNil(order.counterpartContactId) &&
      accounts.some((candidate) => !candidate.disabled && candidate.contactId === order.counterpartContactId);
    return { replaced: beneficiaryHasAnotherAccount, state: OpenInvoiceIbanState.MISSING };
  }
  if (account.type === BankAccountType.AGENCY_ACCOUNT) {
    return { bankAccountId: account.id, replaced: false, state: OpenInvoiceIbanState.OWN };
  }
  return {
    bankAccountId: account.id,
    replaced: false,
    state: isBankAccountIbanSafe(account) ? OpenInvoiceIbanState.VERIFIED : OpenInvoiceIbanState.TO_VERIFY,
  };
};

export type BeneficiaryAccountOrderFields = Pick<PaymentOrder, 'counterpartContactId' | 'type'>;

export const pickBeneficiaryAccount = <T extends BeneficiaryIbanAccountFields>(
  order: BeneficiaryAccountOrderFields,
  accounts: T[]
): T | undefined => {
  const { counterpartContactId } = order;
  if (isNil(counterpartContactId)) {
    return undefined;
  }
  return accounts
    .filter(
      (account) =>
        !account.disabled &&
        account.contactId === counterpartContactId &&
        account.type !== BankAccountType.AGENCY_ACCOUNT &&
        isBeneficiaryAccountOfType(account, order.type)
    )
    .sort(compareByEffectiveIbanStatus)[0];
};

export const APPROVAL_UNLOCKED_IBAN_STATES: OpenInvoiceIbanState[] = [
  OpenInvoiceIbanState.OWN,
  OpenInvoiceIbanState.VERIFIED,
];

export const isIbanApprovalUnlocked = (state: OpenInvoiceIbanState): boolean =>
  APPROVAL_UNLOCKED_IBAN_STATES.includes(state);

export const getApprovableStatuses = (doubleChecksEnabled: boolean): PaymentOrderStatus[] =>
  doubleChecksEnabled
    ? [PaymentOrderStatus.TO_VALIDATE]
    : [PaymentOrderStatus.SUGGESTED, PaymentOrderStatus.TO_VALIDATE];

export type ApprovalOrderFields = Pick<
  PaymentOrder,
  'anomalies' | 'createdBy' | 'status' | 'submittedBy' | 'suggestion' | 'suggestionResolved'
>;

export const hasUnresolvedSuggestion = (order: Pick<PaymentOrder, 'suggestion' | 'suggestionResolved'>): boolean =>
  !isNil(order.suggestion) && order.suggestionResolved !== true;

export interface ApprovalContext {
  callerId?: string | null;
  doubleChecksEnabled: boolean;
  ibanState: OpenInvoiceIbanState;
}

export const isSelfApprovalForbidden = (order: Pick<PaymentOrder, 'submittedBy'>, context: ApprovalContext): boolean =>
  context.doubleChecksEnabled && !isNil(order.submittedBy) && order.submittedBy === context.callerId;

/** Without double checks only the preparer skips submission; a system-created order has no preparer. */
export const isForeignSuggestedOrder = (
  order: Pick<PaymentOrder, 'createdBy' | 'status'>,
  callerId: string | null | undefined
): boolean => order.status === PaymentOrderStatus.SUGGESTED && !isNil(order.createdBy) && order.createdBy !== callerId;

export const isBulkApprovable = (order: ApprovalOrderFields, context: ApprovalContext): boolean =>
  getApprovableStatuses(context.doubleChecksEnabled).includes(order.status) &&
  !isSelfApprovalForbidden(order, context) &&
  !isForeignSuggestedOrder(order, context.callerId) &&
  isIbanApprovalUnlocked(context.ibanState) &&
  !hasUnresolvedSuggestion(order) &&
  isEmpty(order.anomalies);
